import { skipToken, useQuery } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type {
  APInvoiceExtractionResult,
  GetAPInvoiceExtractionResultResponse,
  InvoiceChangeLogEntry,
  ListAPInvoiceChangeLogResponse,
  ListAPInvoiceSupportingDocumentsResponse,
  ListAPInvoicesInput,
  ListAPInvoicesResponse,
  GetAPInvoiceResponse,
  SupportingDocItem,
} from '@erp/rpc';

export function useAPInvoicesList(
  filters: ListAPInvoicesInput = {},
  enabled = true,
) {
  const query = useQuery({
    ...orpc.accountsPayable.invoices.list.queryOptions({
      input: {
        ...filters,
        includeNumLineItemsMatched: true,
      },
    }),
    enabled,
  });

  const invoices = query.data?.invoices ?? [];
  const pagination = query.data?.pagination;
  const extensionColumns = query.data?.extensionColumns;

  return {
    ...query,
    invoices,
    pagination,
    extensionColumns,
  } satisfies typeof query & {
    data?: ListAPInvoicesResponse;
    invoices: ListAPInvoicesResponse['invoices'];
    pagination: ListAPInvoicesResponse['pagination'] | undefined;
    extensionColumns: ListAPInvoicesResponse['extensionColumns'];
  };
}

export function useAPInvoice(id: string | undefined, enabled = true) {
  const query = useQuery({
    ...orpc.accountsPayable.invoices.get.queryOptions({
      input: id ? { id } : skipToken,
    }),
    enabled: Boolean(id) && enabled,
  });

  return {
    ...query,
    invoice: query.data ?? null,
  } satisfies typeof query & {
    data?: GetAPInvoiceResponse;
    invoice: GetAPInvoiceResponse | null;
  };
}

export function useAPInvoiceChangeLog(
  apInvoiceId: string | undefined,
  enabled = true,
) {
  const query = useQuery({
    ...orpc.accountsPayable.invoices.listChangeLog.queryOptions({
      input: apInvoiceId ? { apInvoiceId } : skipToken,
    }),
    enabled: Boolean(apInvoiceId) && enabled,
  });

  return {
    ...query,
    entries: query.data?.entries ?? [],
  } satisfies typeof query & {
    data?: ListAPInvoiceChangeLogResponse;
    entries: InvoiceChangeLogEntry[];
  };
}

export function useAPInvoiceExtractionResult(
  id: string | undefined,
  enabled = true,
) {
  const query = useQuery({
    ...orpc.accountsPayable.invoices.getExtractionResult.queryOptions({
      input: id ? { id } : skipToken,
    }),
    enabled: Boolean(id) && enabled,
  });

  return {
    ...query,
    extractionResult: query.data?.extractionResult ?? null,
  } satisfies typeof query & {
    data?: GetAPInvoiceExtractionResultResponse;
    extractionResult: APInvoiceExtractionResult | null;
  };
}

export function useAPInvoiceSupportingDocuments(
  apInvoiceId: string | undefined,
  enabled = true,
) {
  const query = useQuery({
    ...orpc.accountsPayable.invoices.listSupportingDocuments.queryOptions({
      input: apInvoiceId ? { apInvoiceId } : skipToken,
    }),
    enabled: Boolean(apInvoiceId) && enabled,
  });

  return {
    ...query,
    documents: query.data?.documents ?? [],
  } satisfies typeof query & {
    data?: ListAPInvoiceSupportingDocumentsResponse;
    documents: SupportingDocItem[];
  };
}

/**
 * Source-ERP holds on every PO, sales order and load linked to the invoice,
 * matched or not.
 */
export function useAPInvoiceHolds(apInvoiceId: string | undefined) {
  const query = useQuery({
    ...orpc.accountsPayable.invoices.listHolds.queryOptions({
      input: apInvoiceId ? { apInvoiceId } : skipToken,
    }),
    enabled: Boolean(apInvoiceId),
  });
  return {
    holds: query.data?.holds ?? [],
    isPending: query.isPending,
    isError: query.isError,
  };
}

export function useAPInvoiceExceptions(
  invoiceId: string | undefined,
  enabled = true,
) {
  const query = useQuery({
    ...orpc.accountsPayable.invoices.listExceptions.queryOptions({
      input: invoiceId ? { invoiceId } : skipToken,
    }),
    enabled: Boolean(invoiceId) && enabled,
  });

  return {
    exceptions: query.data?.exceptions ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}
